import { openDB } from 'idb';

const DB_NAME = 'alex-chat-db';
const STORE_NAME = 'messages';
const DB_VERSION = 2; // Incremented version due to schema change (adding index)

export async function initDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      console.log(`Upgrading DB from version ${oldVersion} to ${newVersion}`);
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        // If the store doesn't exist, create it with the keyPath and the new index
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('conversationId', 'conversationId', { unique: false });
        console.log('Created object store and conversationId index.');
      } else {
        // If the store exists, check if the index needs to be added (for upgrades from version 1)
        const store = transaction.objectStore(STORE_NAME);
        if (!store.indexNames.contains('conversationId')) {
          store.createIndex('conversationId', 'conversationId', { unique: false });
          console.log('Created conversationId index on existing store.');
        }
      }
    }
  });
}

/**
 * Saves a message to the database for a specific conversation.
 * @param {object} message - The message object to save.
 * @param {string} conversationId - The ID of the conversation this message belongs to.
 */
export async function saveMessage(message, conversationId) {
  if (!conversationId) {
    console.error('saveMessage error: conversationId is required.');
    return;
  }
  const db = await initDB();
  // Ensure the message object includes the conversationId
  const messageToStore = {
    ...message,
    conversationId: conversationId, // Explicitly add/overwrite conversationId
    timestamp: message.timestamp || new Date() // Use existing timestamp or create new
  };
  // console.log('Saving message with conversationId:', messageToStore);
  await db.add(STORE_NAME, messageToStore);
}

/**
 * Retrieves all messages for a specific conversation.
 * @param {string} conversationId - The ID of the conversation to fetch messages for.
 * @returns {Promise<Array>} A promise that resolves to an array of messages.
 */
export async function getAllMessages(conversationId) {
  if (!conversationId) {
    console.error('getAllMessages error: conversationId is required.');
    return []; // Return empty array if no conversationId is provided
  }
  const db = await initDB();
  // Use the 'conversationId' index to get all messages for the given conversationId
  const messages = await db.getAllFromIndex(STORE_NAME, 'conversationId', conversationId);
  // console.log(`Fetched messages for conversationId ${conversationId}:`, messages);
  return messages || []; // Ensure an array is always returned
}

/**
 * Clears all messages from the store.
 * Note: This clears messages for ALL conversations.
 */
export async function clearMessages() {
  const db = await initDB();
  await db.clear(STORE_NAME);
  console.log('Cleared all messages from the database.');
}

// Optional: Function to delete messages for a specific conversationId
// This is not currently used by ChatInterface.js but might be useful.
export async function clearMessagesByConversationId(conversationId) {
  if (!conversationId) {
    console.error('clearMessagesByConversationId error: conversationId is required.');
    return;
  }
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const index = tx.store.index('conversationId');
  let cursor = await index.openCursor(IDBKeyRange.only(conversationId));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
  console.log(`Cleared messages for conversationId: ${conversationId}`);
}
